// src/context/NFTContext.js

import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useContext
} from 'react';
import { WalletContext } from './WalletContext';
import { MiningContext } from './MiningContext';
import { StakingContext } from './StakingContext';
import {
  loadCashNFTContract,
  loadCashSwapContract,
  loadStakingContract,
} from '../utils/loadBlockchain';
import { Typography } from '@mui/material';
import {
  CASHNFT_ADDRESS,
  CASHSTAKING_ADDRESS,
  CASHSWAP_ADDRESS,
  NETWORK_CONFIG
} from '../contracts/constants';
import { toast } from 'react-toastify';

export const NFTContext = createContext();

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
  },
};

export const NFTProvider = ({ children }) => {
  const { account, provider, chainId } = useContext(WalletContext);
  const { userContributions, isInitialized: isMiningInitialized } = useContext(MiningContext);
  const { isInitialized: isStakingInitialized } = useContext(StakingContext);

  const { contributionCount, minerNumber } = userContributions;

  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const [userNFTs, setUserNFTs] = useState([]);
  const [ogMinerNFTs, setOgMinerNFTs] = useState([]);
  const [og49erNFTs, setOg49erNFTs] = useState([]);
  const [ogSwapperNFTs, setOgSwapperNFTs] = useState([]);
  const [councilNFTs, setCouncilNFTs] = useState([]);

  const [loadingNFTs, setLoadingNFTs] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [isOGMinerEligible, setIsOGMinerEligible] = useState(false);
  const [isOG49erEligible, setIsOG49erEligible] = useState(false);
  const [isOGSwapperEligible, setIsOGSwapperEligible] = useState(false);
  const [isCouncilEligible, setIsCouncilEligible] = useState(false);

  const [isTransactionPending, setIsTransactionPending] = useState(false);
  const [hasEligibilityErrorShown, setHasEligibilityErrorShown] = useState(false);

  const nftContractRef = useRef(null);
  const cashSwapContractRef = useRef(null);
  const cashStakingContractRef = useRef(null);
  const contractsInitializedRef = useRef(false);

  const initializeRetryCountRef = useRef(0);
  const MAX_INITIALIZE_ATTEMPTS = 3;
  const isFetchingNFTsRef = useRef(false);
  const isDeterminingEligibilityRef = useRef(false);

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);

  const ShareModal = () => {
    if (!showShareModal || !shareData) return null;
    const handleShare = () => {
      window.open(shareData.shareUrl, '_blank', 'width=550,height=420');
      setShowShareModal(false);
    };
    return (
      <div style={styles.overlay}>
        <div
          style={{
            background: '#1a1a1a',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" style={{ marginBottom: '1rem', color: '#fff' }}>
            🎉 Congratulations!
          </Typography>
          <Typography style={{ marginBottom: '2rem', color: '#fff' }}>
            Would you like to share your OG Miner status on X?
          </Typography>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={handleShare}
              style={{
                background: '#1DA1F2',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Share on X
            </button>
            <button
              onClick={() => setShowShareModal(false)}
              style={{
                background: 'transparent',
                color: 'white',
                border: '1px solid white',
                padding: '0.75rem 1.5rem',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    );
  };

  const retryContractCall = useCallback(async (fn, maxAttempts = 3) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await fn();
      } catch (error) {
        if (
          error.code === -32603 ||
          error?.data?.code === -32094 ||
          (error?.data?.message || "").includes("Blockchain height exceeded")
        ) {
          if (i < maxAttempts - 1) {
            const delay = Math.pow(2, i) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
        }
        throw error;
      }
    }
    return null;
  }, []);

  const categorizeNFT = useCallback((jsonData, nftData) => {
    const nameLower = jsonData.name.toLowerCase();
    if (nameLower.includes('og miner')) return 'ogMinerIds';
    if (nameLower.includes('og 49er')) return 'og49erIds';
    if (nameLower.includes('og swapper')) return 'ogSwapperIds';
    if (nameLower.includes('council')) return 'councilIds';
    return null;
  }, []);

  const fetchUserNFTs = useCallback(
    async (isInitial = false) => {
      if (!nftContractRef.current || !account || isFetchingNFTsRef.current || chainId !== NETWORK_CONFIG.chainId) {
        return;
      }
      isFetchingNFTsRef.current = true;
      let retryCount = 0;
      const maxRetries = 3;

      const fetchWithRetry = async () => {
        try {
          if (isInitial) {
            setLoadingNFTs(true);
          }
          const balance = await retryContractCall(() =>
            nftContractRef.current.balanceOf(account)
          );
          const nftIds = [];
          const ogMinerIds = [];
          const og49erIds = [];
          const ogSwapperIds = [];
          const councilIds = [];
          if (balance.gt(0)) {
            for (let i = 0; i < balance.toNumber(); i++) {
              try {
                const tokenId = await nftContractRef.current.tokenOfOwnerByIndex(account, i);
                let tokenURI;
                let uriRetryCount = 0;
                const maxUriRetries = 3;
                while (uriRetryCount < maxUriRetries) {
                  try {
                    tokenURI = await nftContractRef.current.tokenURI(tokenId);
                    break;
                  } catch (uriError) {
                    uriRetryCount++;
                    if (
                      uriError.code === -32603 ||
                      uriError?.data?.code === -32094 ||
                      (uriError?.data?.message || "").includes("Blockchain height exceeded")
                    ) {
                      if (uriRetryCount < maxUriRetries) {
                        const delay = Math.pow(2, uriRetryCount) * 1000;
                        await new Promise((resolve) => setTimeout(resolve, delay));
                        continue;
                      }
                    }
                    throw uriError;
                  }
                }
                if (tokenURI) {
                  const base64Data = tokenURI.split(',')[1];
                  const jsonData = JSON.parse(atob(base64Data));
                  const imageData = jsonData.image;
                  let imageSrc = '';
                  if (
                    imageData.startsWith('data:image/svg+xml;base64,') ||
                    imageData.startsWith('data:image/png;base64,') ||
                    imageData.startsWith('data:image/jpeg;base64,')
                  ) {
                    imageSrc = imageData;
                  } else {
                    imageSrc = `data:image/svg+xml;base64,${btoa(jsonData.image)}`;
                  }
                  const nftData = {
                    tokenId: tokenId.toString(),
                    tokenURI,
                    imageSrc,
                    name: jsonData.name || 'Unnamed NFT',
                    description: jsonData.description || '',
                  };
                  nftIds.push(nftData);
                  const category = categorizeNFT(jsonData, nftData);
                  if (category) {
                    switch (category) {
                      case 'ogMinerIds':
                        ogMinerIds.push(nftData);
                        break;
                      case 'og49erIds':
                        og49erIds.push(nftData);
                        break;
                      case 'ogSwapperIds':
                        ogSwapperIds.push(nftData);
                        break;
                      case 'councilIds':
                        councilIds.push(nftData);
                        break;
                      default:
                        break;
                    }
                  }
                }
              } catch (error) {
                console.error('Error fetching token URI or data:', error);
              }
            }
          }
          setUserNFTs(nftIds);
          setOgMinerNFTs(ogMinerIds);
          setOg49erNFTs(og49erIds);
          setOgSwapperNFTs(ogSwapperIds);
          setCouncilNFTs(councilIds);
          return true;
        } catch (error) {
          if (
            error.code === 'CALL_EXCEPTION' ||
            error.code === -32603 ||
            error?.data?.message?.includes("Blockchain height exceeded")
          ) {
            retryCount++;
            if (retryCount < maxRetries) {
              const delay = Math.pow(2, retryCount) * 1000;
              await new Promise((resolve) => setTimeout(resolve, delay));
              return false;
            }
          }
          throw error;
        }
      };

      try {
        let success = false;
        while (!success && retryCount < maxRetries) {
          success = await fetchWithRetry();
        }
      } catch (error) {
      } finally {
        if (isInitial) {
          setLoadingNFTs(false);
          setIsInitialLoad(false);
        }
        isFetchingNFTsRef.current = false;
      }
    },
    [account, chainId, retryContractCall, categorizeNFT]
  );

  const handleError = useCallback((error, message) => {
    console.error(message || error.message || 'An unexpected error occurred.', error);
  }, []);

  const verifyContract = useCallback(
    async (contract, address) => {
      if (!contract || !address) return false;
      try {
        const code = await provider.getCode(address);
        if (code === '0x') {
          return false;
        }
        if (contract.supportsInterface) {
          const supportsERC721 = await contract.supportsInterface('0x80ac58cd');
          if (!supportsERC721) return false;
          const supportsERC721Enumerable = await contract.supportsInterface('0x780e9d63');
          if (!supportsERC721Enumerable) {
            throw new Error('NFT contract does not support ERC721Enumerable interface.');
          }
        }
        return true;
      } catch {
        return false;
      }
    },
    [provider]
  );

  const initializeWithRetry = useCallback(
    async (loadFn, maxRetries = 5) => {
      let retryCount = 0;
      while (retryCount < maxRetries) {
        try {
          const contract = await loadFn(provider);
          if (contract) return contract;
        } catch (error) {
          retryCount++;
          const isRPCError =
            error.code === -32603 ||
            error?.data?.code === -32094 ||
            (error?.data?.message || "").includes("Blockchain height exceeded");
          if (isRPCError && retryCount < maxRetries) {
            const delay = Math.pow(2, retryCount) * 2000;
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
          throw error;
        }
      }
      return null;
    },
    [provider]
  );

  const initializeNFTContract = useCallback(async () => {
    if (!provider || !account || chainId !== NETWORK_CONFIG.chainId || initializeRetryCountRef.current >= MAX_INITIALIZE_ATTEMPTS) {
      return;
    }
    setIsInitializing(true);
    try {
      const [nftContract, swapContract, stakingContractInstance] = await Promise.all([
        initializeWithRetry(loadCashNFTContract),
        initializeWithRetry(loadCashSwapContract),
        initializeWithRetry(loadStakingContract),
      ]);
      if (!nftContract || !swapContract || !stakingContractInstance) {
        throw new Error('Failed to initialize one or more contracts');
      }
      const nftVerified = await verifyContract(nftContract, CASHNFT_ADDRESS);
      const swapVerified = await verifyContract(swapContract, CASHSWAP_ADDRESS);
      const stakingVerified = await verifyContract(stakingContractInstance, CASHSTAKING_ADDRESS);
      if (!nftVerified || !swapVerified || !stakingVerified) {
        throw new Error('Contract verification failed');
      }
      nftContractRef.current = nftContract;
      cashSwapContractRef.current = swapContract;
      cashStakingContractRef.current = stakingContractInstance;
      contractsInitializedRef.current = true;
      initializeRetryCountRef.current = 0;
      setIsInitialized(true);
      await fetchUserNFTs(true);
    } catch (error) {
      initializeRetryCountRef.current += 1;
      const isRPCError =
        error.code === -32603 ||
        error?.data?.code === -32094 ||
        (error?.data?.message || "").includes("Blockchain height exceeded");
      if (isRPCError) {
        if (process.env.NODE_ENV === 'development') {
        }
      }
      handleError(error, error.message || 'Failed to initialize contracts');
      setIsInitialized(false);
      contractsInitializedRef.current = false;
    } finally {
      setIsInitializing(false);
    }
  }, [provider, account, chainId, fetchUserNFTs, handleError, verifyContract, initializeWithRetry]);

  useEffect(() => {
    if (!isMiningInitialized || !isStakingInitialized || isInitializing || isInitialized) return;
    initializeNFTContract();
  }, [isMiningInitialized, isStakingInitialized, isInitializing, isInitialized, initializeNFTContract]);

  useEffect(() => {
    if (!provider || !account) {
      if (isInitialized) {
        setUserNFTs([]);
        setOgMinerNFTs([]);
        setOg49erNFTs([]);
        setOgSwapperNFTs([]);
        setCouncilNFTs([]);
        nftContractRef.current = null;
        cashSwapContractRef.current = null;
        cashStakingContractRef.current = null;
        setIsInitialized(false);
        contractsInitializedRef.current = false;
        initializeRetryCountRef.current = 0;
      }
    }
  }, [provider, account, isInitialized]);

  const checkOGMinerEligibility = useCallback(async () => {
    if (!nftContractRef.current) return false;
    try {
      const hasMinted = await retryContractCall(() =>
        nftContractRef.current.hasMintedOGMiner(account)
      );
      return !hasMinted && contributionCount > 0;
    } catch (error) {
      if (!hasEligibilityErrorShown) {
        handleError(error, 'Failed to check OG Miner eligibility.');
        setHasEligibilityErrorShown(true);
      }
      return false;
    }
  }, [account, contributionCount, retryContractCall, handleError, hasEligibilityErrorShown]);

  const checkOG49erEligibility = useCallback(async () => {
    if (!nftContractRef.current || !cashStakingContractRef.current) return false;
    try {
      const hasMinted = await retryContractCall(() =>
        nftContractRef.current.hasMintedOG49er(account)
      );
      const hasStakedMaxPeriod = await retryContractCall(() =>
        cashStakingContractRef.current.hasUserStakedMaxPeriodInFirst3Days(account)
      );
      return !hasMinted && hasStakedMaxPeriod;
    } catch (error) {
      if (!hasEligibilityErrorShown) {
        handleError(error, 'Failed to check OG 49er eligibility.');
        setHasEligibilityErrorShown(true);
      }
      return false;
    }
  }, [account, retryContractCall, handleError, hasEligibilityErrorShown]);

  const checkOGSwapperEligibility = useCallback(async () => {
    if (!nftContractRef.current || !cashSwapContractRef.current) return false;
    try {
      const hasMinted = await retryContractCall(() =>
        nftContractRef.current.hasMintedOGSwapper(account)
      );
      const hasSwapped = await retryContractCall(() =>
        cashSwapContractRef.current.hasUserSwappedInFirst3Days(account)
      );
      return !hasMinted && hasSwapped;
    } catch (error) {
      if (!hasEligibilityErrorShown) {
        handleError(error, 'Failed to check OG Swapper eligibility.');
        setHasEligibilityErrorShown(true);
      }
      return false;
    }
  }, [account, retryContractCall, handleError, hasEligibilityErrorShown]);

  const checkCouncilEligibility = useCallback(async () => {
    if (!nftContractRef.current) return false;
    try {
      const isCouncilMember = await retryContractCall(() =>
        nftContractRef.current.isCouncilMember(account)
      );
      const hasMinted = await retryContractCall(() =>
        nftContractRef.current.hasMintedCouncil(account)
      );
      return isCouncilMember && !hasMinted;
    } catch (error) {
      if (!hasEligibilityErrorShown) {
        handleError(error, 'Failed to check Council eligibility.');
        setHasEligibilityErrorShown(true);
      }
      return false;
    }
  }, [account, retryContractCall, handleError, hasEligibilityErrorShown]);

  const determineEligibility = useCallback(async () => {
    if (!account || isDeterminingEligibilityRef.current || !nftContractRef.current || !isInitialized) {
      return;
    }
    isDeterminingEligibilityRef.current = true;

    try {
      const [ogMinerEligible, og49erEligible, ogSwapperEligible, councilEligible] = await Promise.all([
        checkOGMinerEligibility(),
        checkOG49erEligibility(),
        checkOGSwapperEligibility(),
        checkCouncilEligibility(),
      ]);

      setIsOGMinerEligible(ogMinerEligible);
      setIsOG49erEligible(og49erEligible);
      setIsOGSwapperEligible(ogSwapperEligible);
      setIsCouncilEligible(councilEligible);

      setHasEligibilityErrorShown(false);
    } catch {
    } finally {
      isDeterminingEligibilityRef.current = false;
    }
  }, [
    account,
    checkOGMinerEligibility,
    checkOG49erEligibility,
    checkOGSwapperEligibility,
    checkCouncilEligibility,
    isInitialized,
  ]);

  useEffect(() => {
    if (account && contractsInitializedRef.current) {
      determineEligibility();
    }
  }, [contributionCount, account, determineEligibility]);

  useEffect(() => {
    initializeNFTContract();
  }, [initializeNFTContract]);

  const setupEventListeners = useCallback(() => {
    if (!nftContractRef.current || !isInitialized) return;
    try {
      const handleTransfer = (from, to) => {
        if (
          from.toLowerCase() === account.toLowerCase() ||
          to.toLowerCase() === account.toLowerCase()
        ) {
          fetchUserNFTs();
          determineEligibility();
        }
      };
      nftContractRef.current.on('Transfer', handleTransfer);
      return () => {
        if (nftContractRef.current) {
          nftContractRef.current.off('Transfer', handleTransfer);
        }
      };
    } catch (error) {
      console.error('Error setting up event listeners:', error);
    }
  }, [account, fetchUserNFTs, determineEligibility, isInitialized]);

  useEffect(() => {
    const cleanup = setupEventListeners();
    return () => {
      if (cleanup) cleanup();
    };
  }, [setupEventListeners]);

  const handleMint = useCallback(
    async (mintFunction, eligibilityCheck, successMessage, errorMessage, options = {}) => {
      if (!nftContractRef.current || !account || !isInitialized) {
        return;
      }
      try {
        const eligible = await eligibilityCheck();
        if (!eligible) {
          return;
        }
        setIsTransactionPending(true);
        const signer = provider.getSigner();
        const contractWithSigner = nftContractRef.current.connect(signer);
        const tx = await mintFunction(contractWithSigner, options);
        await tx.wait();
        toast.success(successMessage);
        if (successMessage.includes('OG Miner')) {
          const shareText = encodeURIComponent(
            `🎉 Just minted my OG Miner NFT!\n\n` +
            `I'm OG Miner #${minerNumber} ⛏️\n\n` +
            `Join the revolution @_CASHisDEAD_ 💸`
          );
          const shareUrl = `https://twitter.com/intent/tweet?text=${shareText}`;
          setShareData({ shareUrl });
          setShowShareModal(true);
        }
        await fetchUserNFTs().catch(() => {});
        determineEligibility().catch(() => {});
        return true;
      } catch (error) {
        toast.error(errorMessage, {
          autoClose: 3000,
          hideProgressBar: true,
        });
        throw error;
      } finally {
        setIsTransactionPending(false);
      }
    },
    [provider, account, isInitialized, fetchUserNFTs, determineEligibility, minerNumber]
  );

  const mintOGMinerNFT = useCallback(() => {
    return handleMint(
      async (contract) => contract.mintOGMinerNFT(),
      checkOGMinerEligibility,
      'Successfully minted OG Miner NFT!',
      'Failed to mint OG Miner NFT.'
    );
  }, [handleMint, checkOGMinerEligibility]);

  const mintOG49erNFT = useCallback(() => {
    return handleMint(
      async (contract) => contract.mintOG49erNFT(),
      checkOG49erEligibility,
      'Successfully minted OG 49er NFT!',
      'Failed to mint OG 49er NFT.'
    );
  }, [handleMint, checkOG49erEligibility]);

  const mintOGSwapperNFT = useCallback(() => {
    return handleMint(
      async (contract) => contract.mintOGSwapperNFT(),
      checkOGSwapperEligibility,
      'Successfully minted OG Swapper NFT!',
      'Failed to mint OG Swapper NFT.'
    );
  }, [handleMint, checkOGSwapperEligibility]);

  const mintCouncilNFT = useCallback(() => {
    return handleMint(
      async (contract) => contract.mintCouncilNFT(),
      checkCouncilEligibility,
      'Successfully minted Council NFT!',
      'Failed to mint Council NFT.'
    );
  }, [handleMint, checkCouncilEligibility]);

  const contextValue = useMemo(
    () => ({
      userNFTs,
      ogMinerNFTs,
      og49erNFTs,
      ogSwapperNFTs,
      councilNFTs,
      loadingNFTs: loadingNFTs || isInitializing,
      isInitialized,
      isInitialLoad,
      fetchUserNFTs,
      mintOGMinerNFT,
      mintOG49erNFT,
      mintOGSwapperNFT,
      mintCouncilNFT,
      isOGMinerEligible,
      isOG49erEligible,
      isOGSwapperEligible,
      isCouncilEligible,
      isTransactionPending,
      determineEligibility,
      nftContract: nftContractRef.current,
    }),
    [
      userNFTs,
      ogMinerNFTs,
      og49erNFTs,
      ogSwapperNFTs,
      councilNFTs,
      loadingNFTs,
      isInitializing,
      isInitialized,
      isInitialLoad,
      fetchUserNFTs,
      mintOGMinerNFT,
      mintOG49erNFT,
      mintOGSwapperNFT,
      mintCouncilNFT,
      isOGMinerEligible,
      isOG49erEligible,
      isOGSwapperEligible,
      isCouncilEligible,
      isTransactionPending,
      determineEligibility,
    ]
  );

  return (
    <NFTContext.Provider value={contextValue}>
      {children}
      <ShareModal />
    </NFTContext.Provider>
  );
};

export default NFTProvider;